import { useEffect } from 'react';
import { PageMeta } from '@/types/seo.types';

const DEFAULT_OG_IMAGE = "https://datahireclub.com.br/og-image.png";
const CANONICAL_BASE = "https://datahireclub.com.br";

/**
 * Builds the canonical URL for the current route, stripping query params
 * (utm_*, filters, etc.) and hash fragments and trailing slashes (except for
 * the root) to avoid duplicate-content issues.
 */
const stripQueryAndHash = (value: string) => value.split('?')[0].split('#')[0];

const normalizePath = (path: string) => {
  let p = stripQueryAndHash(path || '/');
  if (p.length > 1 && p.endsWith('/')) p = p.replace(/\/+$/, '');
  return p;
};

const buildAutoCanonical = (): string => {
  if (typeof window === 'undefined') return CANONICAL_BASE;
  return `${CANONICAL_BASE}${normalizePath(window.location.pathname)}`;
};

const sanitizeCanonical = (value: string): string => {
  if (value.startsWith('http')) {
    try {
      const url = new URL(value);
      return `${url.origin}${normalizePath(url.pathname)}`;
    } catch {
      return stripQueryAndHash(value);
    }
  }
  return `${CANONICAL_BASE}${normalizePath(value)}`;
};

export const usePageMeta = ({
  title,
  description,
  canonical,
  ogImage,
  noindex = false,
  type = 'website',
}: PageMeta) => {
  useEffect(() => {
    // Set document title
    document.title = title;

    const finalOgImage = ogImage || DEFAULT_OG_IMAGE;

    // Set or update meta tags
    const setMetaTag = (property: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${property}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, property);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Basic meta tags
    setMetaTag('description', description);
    
    // Open Graph tags
    setMetaTag('og:title', title, true);
    setMetaTag('og:description', description, true);
    setMetaTag('og:type', type, true);
    
    if (finalOgImage) {
      setMetaTag('og:image', finalOgImage, true);
    }
    
    // og:url is set below alongside the resolved canonical


    // Twitter Card tags
    setMetaTag('twitter:card', 'summary_large_image');
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    
    if (finalOgImage) {
      setMetaTag('twitter:image', finalOgImage);
    }

    // Canonical URL — auto-derive from current path when not provided and the
    // page is indexable. Always strip query params (utm_*, filtros) and hash
    // fragments so the canonical points to the clean URL.
    let resolvedCanonical: string | undefined;
    if (canonical) {
      resolvedCanonical = sanitizeCanonical(canonical);
    } else if (!noindex) {
      resolvedCanonical = buildAutoCanonical();
    }

    // Remove any pre-existing canonical to avoid duplicates from index.html or
    // previous routes, then set the resolved one (when applicable).
    document.querySelectorAll('link[rel="canonical"]').forEach((el) => el.remove());
    if (resolvedCanonical && !noindex) {
      const canonicalElement = document.createElement('link');
      canonicalElement.rel = 'canonical';
      canonicalElement.href = resolvedCanonical;
      document.head.appendChild(canonicalElement);
    }

    if (resolvedCanonical) {
      setMetaTag('og:url', resolvedCanonical, true);
    }

    // Robots meta tag
    if (noindex) {
      setMetaTag('robots', 'noindex, nofollow');
    } else {
      const robotsElement = document.querySelector('meta[name="robots"]');
      if (robotsElement) {
        robotsElement.remove();
      }
    }
  }, [title, description, canonical, ogImage, noindex, type]);
};
